import { Helmet } from "react-helmet";
import PropTypes from "prop-types";

/**
 * SEO component for setting metadata tags for search engines and social media platforms.
 * @param { string } title - The title of the page.
 * @param { string } description - The description of the page. (Default: Discover the latest insights and developments in the metaverse and blockchain technology with MeetusVR. Stay informed about virtual reality, decentralized finance, NFTs, and more.)
 * @param { Array } keywords - The keywords of the page. (Default: ["MeetusVR", "metaverse", "virtual reality", "blockchain", "decentralized finance", "NFTs"])
 * @param { string } name - The name of the website. (Default: MeetusVR)
 * @param { string } type - The type of the website. (Default: WebSite)
 * @param { string } link - The link of the website. (Default: https://meetusvr.com)
 * @param { Object: { summary, username } } twitterData - The twitter data of the website. (Default: { summary: "", username: "@MeetusVR" })
 * @returns { React.JSX } {Element} - Returns the SEO component.
 * @constructor - Returns the SEO component.
 */
export default function SEO({
    title,
    description,
    keywords,
    name,
    type,
    link,
    twitterData,
}) {
    // Add defensive check for keywords
    const keywordsString = Array.isArray(keywords)
        ? keywords.join(", ")
        : typeof keywords === "string"
        ? keywords
        : "";

    return (
        <Helmet>
            {/* Standard metadata tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="author" content={name} />
            <meta name="keywords" content={keywordsString} />
            <link rel="canonical" href={link} />

            {/* Facebook tags */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />

            {/* Twitter tags */}
            <meta name="twitter:card" content={twitterData.summary} />
            <meta name="twitter:site" content={twitterData.username} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />

            <script type="application/ld+json">
                {`
                {
                    "@context": "http://schema.org",
                    "@type": "${type}",
                    "name": "${name}",
                    "url": "${link}",
                    "description": "${description}"
                }
            `}
            </script>
        </Helmet>
    );
}

SEO.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    keywords: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    twitterData: PropTypes.shape({
        summary: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
    }).isRequired,
};

SEO.defaultProps = {
    title: "MeetusVR",
    description:
        "Discover the latest insights and developments in the metaverse and blockchain technology with MeetusVR. Stay informed about virtual reality, decentralized finance, NFTs, and more.",
    keywords: [
        "MeetusVR",
        "metaverse",
        "virtual reality",
        "blockchain",
        "decentralized finance",
        "NFTs",
    ],
    name: "MeetusVR",
    type: "WebSite",
    link: "https://meetusvr.com",
    twitterData: { summary: "", username: "@MeetusVR" },
};

